import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	FormItem,
	Button,
	Space,
	Modal,
	ModalTitle,
	Select,
	Checkbox,
	Textbox,
	Tabs,
	Tab,
	Textarea,
	Player,
} from '@components';
import {
	apiInstance,
	DuplicateRequest,
	MeetingInfo,
	MeetingStatuses,
	MeetingStatusInfo,
	RecordingState,
	RecordingUpdateInfo,
	TranscriptRequestOptions
} from '@api';

import './MeetingDevModal.scss';
import { toast } from 'react-toastify';
import { DictionaryItem, stringToHexColor } from '@common';

interface MeetingDevModalProps {
	closeModal: () => void,
	meetingInfo: MeetingInfo,
	transcriptRequestOptions: TranscriptRequestOptions,
	changeTranscriptRequestOptions: (newOptions: TranscriptRequestOptions) => void,
	purgeSummary: () => void,
	requestASR: (request: RecordingUpdateInfo) => void,
	cloneMeeting: (request: DuplicateRequest) => void,
	makeFineTunnedAuto: (fineTuneParams: string) => void,
	setUserVoice: (userVoice: File) => void,
	playerBoxRef: React.MutableRefObject<HTMLVideoElement>,
	purgeObsoleteMedia: () => void,
	deleteMeeting: () => Promise<void>,
}

class StatusOption
{
	constructor(title: string, value: string) {
		this.title = title;
		this.value = value;
	}
	title: string;
	value: string;
}

function MeetingDevModal(props: MeetingDevModalProps): JSX.Element {
	const {
		closeModal,
		meetingInfo,
		transcriptRequestOptions,
		changeTranscriptRequestOptions,
		purgeSummary,
		requestASR,
		cloneMeeting,
		makeFineTunnedAuto,
		setUserVoice,
		playerBoxRef,
		purgeObsoleteMedia,
		deleteMeeting,
	} = props;

	const DiarizaionKinds = [
		new DictionaryItem('Disabled', 0),
		new DictionaryItem('ASR', 1),
		new DictionaryItem('MultiChannel', 2),
	];


	const [asrForce, setASRForce] = useState<boolean>(false);
	const [punctuationForce, setPunctuationForce] = useState<boolean>(false);
	const [asrName, setASRName] = useState<string>("");
	const [asrModel, setASRModel] = useState<string>("");
	const [asrDiarization, setAsrDiarization] = useState<DictionaryItem<number>>(DiarizaionKinds[0]);
	const [asrMaxDiarizedSpeakers, setAsrMaxDiarizedSpeakers] = useState<string>("6");
	const [asrLanguageCode, setAsrLanguageCode] = useState<string>("ru-RU");
	const [asrMinLoudness, setAsrMinLoudness] = useState<string>('');
	const [asrMinConfidence, setAsrMinConfidence] = useState<string>("0.0");

	const [duplicateRequest, setDuplicateRequest] = useState<DuplicateRequest>(new DuplicateRequest({subjectSuffix: '(копия)', copyTranscripts: false, copySummary: false }));
	const [speakerFiles, setSpeakerFiles] = useState<string[]>([]);
	const [speakerWaves, setSpeakerWaves] = useState<{[key: string]: string}>({});
	const [spakerNames, setSpeakerNames] = useState<{[key: string]: string}>({});
	const [supportedAsrs, setSupportedAsrs] = useState<string[]>([]);
	
	// See /summarization/app/SummaryServiceApi_server.py
	const [currentSummaryParams, setCurrentSummaryParams] = useState<string>('{\n"model": "extractive",\n"tokenize": "utterance",\n"ExtractNER": true,\n"CropCommands": false\n}');
	
	const [recorder, setRecorder] = useState(null as MediaRecorder);
	const [isRecording, setIsRecording] = useState<boolean>(false);
	const filePlayerRef = useRef<HTMLAudioElement>(null);
	const [recordingFile, setRecordingFile] = useState<File>(null);
	const [recordingFileUri, setRecordingFileUri] = useState<string>(null);
	const [meetingStatuses, setMeetingStatuses] = useState<MeetingStatusInfo[]>([]);


	useEffect(() => {
		async function fetchMeetingInfo(): Promise<void> {
			try
			{				
				const supportedAsrsVal = await apiInstance.getSupportedASRs();
				setSupportedAsrs(supportedAsrsVal);


				const speakerFiles = await apiInstance.getSpeakersFiles(meetingInfo.id);
				const speakerFileNames = speakerFiles.map(sf => sf.fileName);
				setSpeakerFiles(speakerFileNames);

				const recordingDurationSeconds = playerBoxRef.current.duration;
				const pixelsPerSecond = 1;
				const speakerTrackWidth = Math.ceil(recordingDurationSeconds * pixelsPerSecond) || 250;

				const speakerWavesTmp = {};
				const speakerNamesTmp = {};
				for (const sfn of speakerFileNames) {
					const participant = await apiInstance.getWaveSpeakerName(meetingInfo.id, sfn);
					const spakerWaveKey = participant?.name || sfn;
					speakerNamesTmp[sfn] = spakerWaveKey;

					const image = await apiInstance.getWaveFormPng(meetingInfo.id, sfn, speakerTrackWidth, 70, stringToHexColor(sfn, 100, 20));
					speakerWavesTmp[sfn] = URL.createObjectURL(image.data);
				}
				setSpeakerWaves(speakerWavesTmp);
				setSpeakerNames(speakerNamesTmp);
			}
			catch(ex)
			{
				toast.error(`${ex}`, {autoClose: false });
			}
		}

		fetchMeetingInfo();
		
	}, [meetingInfo, setSpeakerFiles, setSpeakerWaves,
		setSupportedAsrs, setRecordingFile, setRecordingFileUri, setSpeakerNames,
		playerBoxRef]);
		
	useEffect(() => {
		async function initRecorder() {
			setMeetingStatuses(await apiInstance.getAllStatuses());

			const stream = await navigator.mediaDevices.getUserMedia({
				audio: true,
			});

			const audioChunks = [];

			const mediaRecorder = new MediaRecorder(stream);
			mediaRecorder.addEventListener("dataavailable", event => {
				audioChunks.push(event.data);
			});

			const onStop = () => {
				let defaultExtension = "webm";
				const mimeMatch = mediaRecorder.mimeType.match(/audio\/([^;]+)/);
				if (mimeMatch && mimeMatch.length) {
					defaultExtension = mimeMatch[1];
				}
				
				const audioFile = new File(audioChunks, `SumMeetFromBrowser.${defaultExtension}`);
				const audioUrl = URL.createObjectURL(audioFile);

				setRecordingFile(audioFile)
				setRecordingFileUri(audioUrl);
				filePlayerRef.current.load();
			};
			mediaRecorder.addEventListener("stop", onStop);
			setRecorder(mediaRecorder);
		}

		initRecorder();
	}, [setRecordingFile, setRecordingFileUri]);

	const headerView = useMemo((): JSX.Element => {
		return (
			<div className="meeting-dev-modal__header">
				<ModalTitle onClose={closeModal}>
					🏗️Режим разработчика🚧
				</ModalTitle>
			</div>
		);
	}, [closeModal]);

	const footerView = useMemo(() => {
		return (
			<Space>
				<Button onClick={closeModal}>
					Закрыть
				</Button>
				<div></div>
			</Space>
		);
	}, [closeModal]);


	const onMergeUttsChange = useCallback((newValue): void => {
		const updatedOptions = new TranscriptRequestOptions({
			...transcriptRequestOptions,
			mergeUtts: newValue,
			reconstructUtts: newValue ? false : transcriptRequestOptions.reconstructUtts,
		});

		changeTranscriptRequestOptions(updatedOptions);

	}, [transcriptRequestOptions, changeTranscriptRequestOptions]);

	const onReconstructUttsChange = useCallback((newValue): void => {
		const updatedOptions = new TranscriptRequestOptions({
			...transcriptRequestOptions,
			reconstructUtts: transcriptRequestOptions.mergeUtts ? false : newValue,
		});

		changeTranscriptRequestOptions(updatedOptions);

	}, [transcriptRequestOptions, changeTranscriptRequestOptions]);
	

	const onRequestASRClick = useCallback(() => {
		const request = new RecordingUpdateInfo({
			recordingId: meetingInfo.id,
			state: RecordingState.Uploaded,
			force: asrForce,
			asrName: asrName,
			asrModel: asrModel,
			asrBatchSeconds: 0,
			diarization: asrDiarization.value,
			maxDiarizedSpeakers: parseInt(asrMaxDiarizedSpeakers),
			languageCode: asrLanguageCode,
			minLoudness: parseFloat(asrMinLoudness),
			minConfidence: parseFloat(asrMinConfidence),
			forcePunctuation: punctuationForce,
		});
		requestASR(request);
	}, [asrName, asrForce, meetingInfo, asrModel, asrDiarization, asrMaxDiarizedSpeakers,
		asrLanguageCode, asrMinLoudness, asrMinConfidence, punctuationForce, requestASR]);

	const cloneMeetingClick = useCallback(() => {
		cloneMeeting(duplicateRequest);
	}, [cloneMeeting, duplicateRequest]);

	const copySummaryChanged = useCallback((newValue) => {
		setDuplicateRequest(new DuplicateRequest({
			...duplicateRequest,
			copySummary: newValue
		}));
	}, [setDuplicateRequest, duplicateRequest]);

	const copyTranscriptChanged = useCallback((newValue) => {
		setDuplicateRequest(new DuplicateRequest({
			...duplicateRequest,
			copyTranscripts: newValue
		}));
	}, [setDuplicateRequest, duplicateRequest]);

	const cloneSuffixChanged = useCallback((newValue) => {
		setDuplicateRequest(new DuplicateRequest({
			...duplicateRequest,
			subjectSuffix: newValue
		}));
	}, [setDuplicateRequest, duplicateRequest]);

	const speakerFilesView = useMemo(() => {
		return speakerFiles.map(sfn => {
			if (sfn in speakerWaves) {
				const imgUrl = speakerWaves[sfn];

				return (<div key={sfn}>{spakerNames[sfn]}<br/>
						<img src={imgUrl}/></div>)
			} else {
				return <div key={sfn}>{spakerNames[sfn]}</div>;
			}
		});
	}, [speakerFiles, speakerWaves, spakerNames]);

	
	const statuses = meetingStatuses.map(ms => {
		return new StatusOption(ms.name, ms.alias);
	});

	const setNewStatus = useCallback((newStatusAlias: StatusOption) => {
		async function set() {
			await apiInstance.developerSetStatus(meetingInfo.id, newStatusAlias.value as MeetingStatuses);
			window.location.reload();
		}

		set();
	}, [meetingInfo]);

	const selectedStatus = statuses.filter(s => s.value == meetingInfo.status.alias)[0];

	const startRecording = useCallback(() => {
		recorder.start();
		setIsRecording(true);

	}, [recorder, setIsRecording]);

	const stopRecording = useCallback(() => {
		if (recorder && recorder.state == "recording") {
			recorder.stop();
			setIsRecording(false);
		}
	}, [recorder]);

	const recorderView = useMemo(() => {
		return (
		<Space>
			{!isRecording && <Button onClick={startRecording} theme="primary">Записать</Button>}
			{isRecording && <Button onClick={stopRecording} theme="primary">Остановить</Button>}
		</Space>)
	}, [isRecording, startRecording, stopRecording]);


	return (meetingInfo &&
		<Modal
			className="meeting-dev-modal"
			header={headerView}
			footer={footerView}
			isOpen
		>
			<div className="meeting-dev-modal__content">
				<Tabs>
					<Tab title="Основное">
						<FormItem label="Статус">
							<Select<StatusOption>
								onChange={setNewStatus}
								textName="title"
								value={selectedStatus}
								options={statuses}
							/>
						</FormItem>
					</Tab>
					<Tab title="Расширенное">						
						<FormItem label="Встреча">
							<Space>
								<Checkbox
									label="Копировать протокол?"
									boolValue={duplicateRequest.copySummary}
									onValueChange={copySummaryChanged}
								/>
								<Checkbox
									label="Копировать стенограмму?"
									boolValue={duplicateRequest.copyTranscripts}
									onValueChange={copyTranscriptChanged}
								/>
							</Space>
							<Space>
								<label htmlFor="subjectSuffix">Суффикс:</label>
								<Textbox id="subjectSuffix" name="subjectSuffix" value={duplicateRequest.subjectSuffix} onValueChange={cloneSuffixChanged} />
								<Button onClick={cloneMeetingClick} theme="primary">Клонировать</Button>
							</Space>
						</FormItem>
						<FormItem label="Сервис">
							<Button onClick={purgeObsoleteMedia} theme="primary">Удалить старые медиа-файлы</Button>
							<Button onClick={deleteMeeting} theme="danger">Удалить собрание</Button>
						</FormItem>
					</Tab>
					<Tab title="Стенограмма">
						<FormItem label="Вариант стенограммы">
							<Checkbox
								label="Склеивать высказывания идущие подряд"
								boolValue={transcriptRequestOptions.mergeUtts}
								onValueChange={onMergeUttsChange}
							/>
							<Checkbox
								label="Реконструировать высказывания"
								boolValue={transcriptRequestOptions.reconstructUtts}
								onValueChange={onReconstructUttsChange}
							/>
						</FormItem>

						<FormItem label="Запросить распознавание" contentClassName='right-align'>
							<Space className='vertical-item'>
							<label htmlFor="asrName">Имя: </label>
							{supportedAsrs ? 
								<Select<string>
									name="asrName"
									onChange={setASRName}
									className="wide-select"
									value={asrName}
									options={supportedAsrs}
								/>
							: <Textbox className="halfsize" id="asrName" name="asrName" value={asrName} onValueChange={setASRName} /> 
							}
							</Space>
							<Space className='vertical-item'>
								<Checkbox
									className="halfsize"
									label="Распознать принудительно"
									boolValue={asrForce}
									onValueChange={setASRForce}
								/>
							</Space>
							<Space className='vertical-item'>
								<Checkbox
									className="halfsize"
									label="Сторонний пунктуатор"
									boolValue={punctuationForce}
									onValueChange={setPunctuationForce}
								/>
							</Space>
							<Space className='vertical-item'>
								<label htmlFor="asrModel">Модель: </label>
								<Textbox className="halfsize" id="asrModel" name="asrModel" value={asrModel} onValueChange={setASRModel} />
							</Space>
							<Space className='vertical-item'>
								<label htmlFor="asrDiarization">Диаризация: </label>
								<Select<DictionaryItem<number>>
									name="asrDiarization"
									onChange={setAsrDiarization}
									className="halfsize"
									textName="name"
									value={asrDiarization}
									options={DiarizaionKinds}
								/>
							</Space>
							<Space className='vertical-item'>
								<label htmlFor="asrMaxDiarizedSpeakers">Максимальное <span className='nobr'>кол-во</span> спикеров: </label>
								<Textbox className='halfsize' id="asrMaxDiarizedSpeakers" name="asrMaxDiarizedSpeakers" value={asrMaxDiarizedSpeakers} onValueChange={setAsrMaxDiarizedSpeakers} />
							</Space>
							<Space className='vertical-item'>
								<label htmlFor="asrLanguageCode">Язык: </label>
								<Textbox className='halfsize'  id="asrLanguageCode" name="asrLanguageCode" value={asrLanguageCode} onValueChange={setAsrLanguageCode} />
							</Space>
							<Space className='vertical-item'>
								<label htmlFor="asrMinLoudness">Минимальный уровень звука: </label>
								<Textbox className='halfsize'  id="asrMinLoudness" name="asrMinLoudness" value={asrMinLoudness} onValueChange={setAsrMinLoudness} />
							</Space>
							<Space className='vertical-item'>
								<label htmlFor="asrMinConfidence">Минимальная уверенность: </label>
								<Textbox className='halfsize'  id="asrMinConfidence" name="asrMinConfidence" value={asrMinConfidence} onValueChange={setAsrMinConfidence} />
							</Space>
							<Button onClick={onRequestASRClick} theme="primary">
								Распознать
							</Button>
						</FormItem>
					</Tab>
					<Tab title="Протокол">
						<FormItem label="Вариант протокола">
							{meetingInfo.summaryBlocks && 
							<Space>
								<div>
									{meetingInfo.summaryMeta && (<>{JSON.stringify(meetingInfo.summaryMeta)}</>)}
								</div>
							</Space>}
						</FormItem>

						<FormItem label="Авторезюме">
							<Textarea value={currentSummaryParams} onValueChange={(newParams) => setCurrentSummaryParams(newParams)} className="textarea"></Textarea>
							<Button onClick={() => makeFineTunnedAuto(currentSummaryParams)} theme="primary">Резюмировать</Button>
						</FormItem>
						<FormItem label="Содержание">
							<Button onClick={purgeSummary} theme="danger">Удалить все пункты</Button>
						</FormItem>
					</Tab>
					<Tab title="Дорожки">
						<FormItem label="Дорожки">
							{speakerFilesView}
						</FormItem>
					</Tab>
					<Tab title="Видео">

					</Tab>
					<Tab title="Голос">
						<Player playerRef={filePlayerRef} mediaUrl={recordingFileUri} onIsPlayingChanged={() => true} onTimeChanged={() => true} />
						{recorderView}
						<br/>
						<FormItem label="Надиктуйте текст:">
						<p className="cite">Привет, Саммит!<br/><br/>
						Я поговорю с тобой немного, чтобы поближе с тобой познакомится.<br/><br/>
						Понадобится чуть больше одного-двух предложений, чтобы ты выучил все особенности моего голоса, и смог распознать его во время собраний.<br/><br/>
						И ничего страшного, если я прочитаю этот текст с огрехами. Если нужно будет перезаписать его, ты дашь мне знать.</p>
						</FormItem>
						<Space>
							<Button onClick={() => setUserVoice(recordingFile)} theme="primary">Запомнить голос</Button>
							<Button onClick={() => setUserVoice(null)} >Забыть мой голос</Button>
						</Space>
					</Tab>
				</Tabs>
				
				
			</div>
		</Modal>
	);
}

export {MeetingDevModal};